import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { Paper, TableRow, TableCell, TableBody } from "@mui/material";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import { usePagination } from "Components/Common/BaseTable";
import FixedRow from "Components/Common/BaseTable/FixedRow";
import Pagination from "Components/Common/BaseTable/Pagination";
import {
  StyledTable,
  StyledTableCell,
  StyledTableHead,
} from "pages/Admin/Reports/style";

import { useLazyPowerAbstractReportQuery } from "apis/Billing";

import styled from "@emotion/styled";

const valuesToFilterForQuery = [null, "", undefined];

const StyledTableContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "maxHeight",
})`
  max-height: ${({ maxHeight }) => maxHeight ?? "unset"};
  overflow-y: auto;
  .MuiPaper-root {
    overflow-x: scroll;
  }
`;

const columns = [
  { title: "Sr. No" },
  { title: "Consumer Number", field: "consumerNumber" },
  { title: "SAP Consumer No", field: "SAP Consumer No" },
  { title: "Consumer Name", field: "consumerName" },
  { title: "Cat of Connection", field: "Cat of Connection" },
  { title: "Unit", field: "unit" },
  { title: "Invoice No.", field: "Invoice No." },
  { title: "Contract Load Kw HT", field: "Contract Load - HT" },
  {
    title: "Billed demand (85% Contract Load - HT)",
    field: "Billed Demand (85% Contract Load - HT)",
  },
  { title: "Actual Max Demand Kva-HT", field: "Actual Max Demand Kva - HT" },
  { title: "Contract Demand - LT", field: "Contract Demand - LT" },
  {
    title: "Billed demand (85% Contract Load Kw LT)",
    field: "Billed Demand (85% Contract Load Kw - LT)",
  },
  { title: "Actual Max Demand kW-LT", field: "Actual Max Demand Kw - LT" },
  { title: "KWH (Energy LT & HT)", field: "KWH (Energy LT & HT)" },
  {
    title: "KVARH (Reactive Energy-LT & HT)",
    field: "KVARH (Reactive Energy - LT & HT)",
  },
  { title: "Export Energy", field: "exportEnergy" },
  { title: "Net Import Energy", field: "netImportEnergy" },
  { title: "Green Energy", field: "greenEnergy" },
  { title: "Percentage of Green Power", field: "Percentage of Green Power" },
  { title: "Peak Hour Energy", field: "peakHourEnergy" },
  { title: "Night Hour Energy", field: "nightHourEnergy" },
  { title: "TOU Discount Energy", field: "touDiscountEnergy" },
  { title: "Billing Load kW", field: "Billing Load kW" },
  { title: "Demand Charges", field: "demandCharges" },
  { title: "Excess Demand Charge", field: "excessDemandCharge" },
  { title: "Demand charge waive off", field: "demandChargeWaiveOff" },
  { title: "Energy Charges", field: "energyCharges" },
  { title: "Night Hour Rebate Charges", field: "Night Hour Rebate Charges" },
  { title: "Green Energy Charges", field: "greenEnergyCharges" },
  { title: "Fuel Surcharges", field: "fuelSurcharges" },
  { title: "Variable Fuel Surcharges", field: "fpppaCharges" },
  { title: "PF Adjustment Charges", field: "pfAdjustmentCharges" },
  { title: "Reactive Energy Charges-LT", field: "Reacive Energy Charges - LT" },
  { title: "Rebate for EHV Supply", field: "Rebate for EHV Supply" },
  { title: "Peak Hour Charges", field: "peakHourCharges" },
  { title: "TOU Discount Charges", field: "touDiscountCharges" },
  { title: "Electricity Duty", field: "electricityDuty" },
  { title: "Electricity Duty Exemption", field: "electricityDutyExemption" },
  { title: "Other", field: "otherAdjustment" },
  { title: "Electricity Duty", field: "electricityDutyAdjustment" },
  { title: "Total", field: "totalAdjustment" },
  { title: "Security Deposit Interest", field: "securityDepositInterest" },
  { title: "Banking Charges", field: "bankingCharges" },
  { title: "Credit Charges", field: "creditCharges" },
  { title: "Delay Payment", field: "delayPayment" },
  { title: "Pending Due", field: "pendingDue" },
  { title: "Net Payable", field: "netPayable" },
  { title: "Current Month Bill", field: "currentMonthBill" },
  { title: "Calculated Electricity Duty", field: "calculatedElectricityDuty" },
];

const PowerAbstractTable = ({
  bodyClassName,
  pageSize,
  registerResetPage,
  powerAbstractTableProps,
  renderSubRow,
  maxHeight,
  t,
}) => {
  const [rowData, setRowData] = useState([]);

  const paginate = usePagination();
  const { page, setTotalPages, first } = paginate;

  const { billingCycleId, query } = powerAbstractTableProps;
  const [
    getPowerAbstractData,
    { data: { data: powerAbstractData } = {}, isLoading, isFetching },
  ] = useLazyPowerAbstractReportQuery();

  useEffect(() => {
    (async () => {
      try {
        const finalQuery = Object.entries({
          ...query,
        }).reduce((result, [key, value]) => {
          if (valuesToFilterForQuery.includes(value)) {
            return result;
          }

          return {
            ...result,
            [key]: value,
          };
        }, {});

        if (billingCycleId) {
          await getPowerAbstractData(billingCycleId, finalQuery);
        }
      } catch (error) {}
    })();
  }, [billingCycleId, query]);

  useEffect(() => {
    if (powerAbstractData) {
      setTotalPages(Math.ceil(powerAbstractData.length / pageSize));
      const rowData = powerAbstractData.slice(
        pageSize * (page - 1),
        pageSize * page
      );
      setRowData(rowData);
    }
  }, [page, pageSize, powerAbstractData, setTotalPages]);

  useEffect(() => {
    registerResetPage(first);
  }, []);

  return (
    <>
      <StyledTableContainer maxHeight={maxHeight}>
        <Backdrop open={isLoading || isFetching} />
        <StyledTable>
          <StyledTableHead
            sx={{
              backgroundColor: "#1B365D",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <TableRow>
              {columns.slice(0, 36)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={4}>
                  {t(header.title)}
                </StyledTableCell>
              ))}
              <StyledTableCell colSpan={3} align="center">
                {t("Adjustments")}
              </StyledTableCell>
              {columns.slice(39, 49)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={3}>
                  {t(`${header.title}`)}
                </StyledTableCell>
              ))}
            </TableRow>
            <TableRow>
              {columns.slice(36, 39)?.map((header, index) => (
                <StyledTableCell key={index} rowSpan={3}>
                  {t(`${header.title}`)}
                </StyledTableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <TableBody className={bodyClassName}>
            {rowData.length ? (
              rowData.map((item, rowIndex) => (
                <FixedRow
                  key={`${item.id}-${page}-${rowIndex}`}
                  rowIndex={rowIndex}
                  columns={columns}
                  page={page}
                  pageSize={pageSize}
                  item={item}
                  displaySrNo={true}
                  renderSubRow={renderSubRow}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center">
                  {t("No Data Available")}!
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      <Pagination paginate={paginate} />
    </>
  );
};

PowerAbstractTable.propTypes = {
  bodyClassName: PropTypes.string,
  pageSize: PropTypes.number,
  powerAbstractTableProps: PropTypes.shape({
    billingCycleId: PropTypes.number,
    query: PropTypes.object,
  }),
  registerResetPage: PropTypes.func,
  renderSubRow: PropTypes.func,
  maxHeight: PropTypes.string,
  t: PropTypes.func,
};

PowerAbstractTable.defaultProps = {
  bodyClassName: "",
  pageSize: 20,
  powerAbstractTableProps: {
    billingCycleId: "",
    query: {},
  },
  registerResetPage: () => {},
  renderSubRow: () => null,
  t: () => {},
};

export default withTranslation()(PowerAbstractTable);
